import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ExportOptions = ({ selectedTransactions, allTransactions }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportFormat, setExportFormat] = useState('json');
  const [exportScope, setExportScope] = useState('all');

  const formatOptions = [
    { value: 'json', label: 'JSON Format', description: 'Machine-readable JSON file' },
    { value: 'csv', label: 'CSV Format', description: 'Spreadsheet-compatible format' },
    { value: 'pdf', label: 'PDF Report', description: 'Human-readable PDF document' },
    { value: 'xml', label: 'XML Format', description: 'Structured XML document' }
  ];

  const scopeOptions = [
    { value: 'all', label: 'All Transactions', description: `Export all ${allTransactions?.length || 0} transactions` },
    { value: 'selected', label: 'Selected Only', description: `Export ${selectedTransactions?.length || 0} selected transactions` },
    { value: 'confirmed', label: 'Confirmed Only', description: 'Export only confirmed transactions' },
    { value: 'recent', label: 'Recent (30 days)', description: 'Export transactions from last 30 days' }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Determine which transactions to export
      let transactionsToExport = [];
      
      switch (exportScope) {
        case 'all':
          transactionsToExport = allTransactions;
          break;
        case 'selected':
          transactionsToExport = allTransactions?.filter(tx => 
            selectedTransactions?.includes(tx?.id)
          );
          break;
        case 'confirmed':
          transactionsToExport = allTransactions?.filter(tx => 
            tx?.status === 'confirmed'
          );
          break;
        case 'recent':
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
          transactionsToExport = allTransactions?.filter(tx => 
            new Date(tx?.timestamp) >= thirtyDaysAgo
          );
          break;
        default:
          transactionsToExport = allTransactions;
      }

      // Create export data
      const exportData = {
        metadata: {
          exportDate: new Date()?.toISOString(),
          totalRecords: transactionsToExport?.length,
          format: exportFormat,
          scope: exportScope,
          exportedBy: 'PolyNotary dApp',
          version: '1.0.0'
        },
        transactions: transactionsToExport?.map(tx => ({
          id: tx?.id,
          originalUrl: tx?.originalUrl,
          title: tx?.title,
          timestamp: tx?.timestamp,
          status: tx?.status,
          transactionHash: tx?.transactionHash,
          ipfsHash: tx?.ipfsHash,
          blockNumber: tx?.blockNumber,
          gasUsed: tx?.gasUsed,
          gasPrice: tx?.gasPrice,
          fileSize: tx?.fileSize,
          verificationStatus: tx?.verificationStatus
        }))
      };

      // Generate file based on format
      let fileContent, mimeType, fileExtension;

      switch (exportFormat) {
        case 'json':
          fileContent = JSON.stringify(exportData, null, 2);
          mimeType = 'application/json';
          fileExtension = 'json';
          break;
        case 'csv':
          const csvHeaders = ['ID', 'URL', 'Title', 'Timestamp', 'Status', 'Transaction Hash', 'IPFS Hash', 'Block Number', 'Gas Used', 'File Size'];
          const csvRows = transactionsToExport?.map(tx => [
            tx?.id,
            `"${tx?.originalUrl}"`,
            `"${tx?.title || ''}"`,
            tx?.timestamp,
            tx?.status,
            tx?.transactionHash,
            tx?.ipfsHash || '',
            tx?.blockNumber || '',
            tx?.gasUsed || '',
            tx?.fileSize || ''
          ]);
          fileContent = [csvHeaders?.join(','), ...csvRows?.map(row => row?.join(','))]?.join('\n');
          mimeType = 'text/csv';
          fileExtension = 'csv';
          break;
        case 'xml':
          fileContent = `<?xml version="1.0" encoding="UTF-8"?>\n<export>\n${exportData?.transactions?.map(tx => 
            `  <transaction id="${tx?.id}">\n    <url>${tx?.originalUrl}</url>\n    <title>${tx?.title || ''}</title>\n    <timestamp>${tx?.timestamp}</timestamp>\n    <status>${tx?.status}</status>\n    <hash>${tx?.transactionHash}</hash>\n  </transaction>`
          )?.join('\n')}\n</export>`;
          mimeType = 'application/xml';
          fileExtension = 'xml';
          break;
        default:
          fileContent = JSON.stringify(exportData, null, 2);
          mimeType = 'application/json';
          fileExtension = 'json';
      }

      // Create and download file
      const blob = new Blob([fileContent], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `polynotary-transactions-${new Date()?.toISOString()?.split('T')?.[0]}.${fileExtension}`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      setShowExportMenu(false);
      
    } catch (error) {
      console.error('Export failed:', error);
      // In a real app, show error message
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        iconName="Download"
        onClick={() => setShowExportMenu(!showExportMenu)}
      >
        Export
      </Button>

      {showExportMenu && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50">
          <div className="p-4 space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Download" size={20} className="text-primary" />
              <h3 className="font-semibold text-foreground">Export Transactions</h3>
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto h-6 w-6"
                onClick={() => setShowExportMenu(false)}
              >
                <Icon name="X" size={14} />
              </Button>
            </div>

            <Select
              label="Export Format"
              value={exportFormat}
              onChange={setExportFormat}
              options={formatOptions}
            />

            <Select
              label="Export Scope"
              value={exportScope}
              onChange={setExportScope}
              options={scopeOptions}
              disabled={selectedTransactions?.length === 0 && exportScope === 'selected'}
            />

            <div className="bg-muted rounded-lg p-3">
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Transactions:</span>
                  <span className="font-medium text-foreground">{allTransactions?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Selected:</span>
                  <span className="font-medium text-foreground">{selectedTransactions?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Format:</span>
                  <span className="font-medium text-foreground uppercase">{exportFormat}</span>
                </div>
              </div>
            </div>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowExportMenu(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                iconName="Download"
                loading={isExporting}
                onClick={handleExport}
                className="flex-1"
              >
                Export
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {showExportMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExportMenu(false)}
        />
      )}
    </div>
  );
};

export default ExportOptions;